import React, { useEffect, useState } from 'react';

import SyncIcon from '@mui/icons-material/Sync';
import WestIcon from '@mui/icons-material/West';
import {
  Alert,
  Backdrop,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import { styled, keyframes } from '@mui/material/styles';
import CryptoJS from 'crypto-js';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import ButtonSx from 'components/button/ButtonSx';
import {
  // useCoohomEnterpriseRegisterMutation,
  // useCoohomSSOMutation,
  useGetExternalUserRegisterStatusQuery,
  // useGetUserCoohomProjectQuery,
  useSyncProjectsWithBaruMutation,
} from 'store/apis/coohom';
import { useNotifyToast } from 'utils/useNotifyToast';

// import CustomCoohomCard from './CustomCoohomCard';

// Define the spin animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const baseUrl = 'https://www.coohom.com/api/saas/openapi/v2/redirect';
const coohomDesignToolURl = '/pub/saas/apps/project/list%26redirectbim=false';
const designUrl = 'https://www.coohom.com/pub/tool/yundesign/cloud';

const SyncingBackdrop = ({ isLoading }) => {
  return (
    <Backdrop
      sx={(theme) => ({
        color: '#fff',
        zIndex: theme.zIndex.drawer + 1,
      })}
      open={isLoading}
    >
      <Stack sx={{ justifyContent: 'center', alignItems: 'center' }} gap={2}>
        <CircularProgress color="inherit" />

        <Typography sx={{ fontSize: 14, opacity: 0.8 }}>
          This may take a few moments. Sync in progress...
        </Typography>
      </Stack>
    </Backdrop>
  );
};

SyncingBackdrop.propTypes = {
  isLoading: PropTypes.bool,
};

// Styled component that conditionally spins
const RotatingSyncIcon = styled(SyncIcon)(({ isLoading }) => ({
  animation: isLoading === true ? `${spin} 2s linear infinite` : 'none',
}));

function getDesignUrl(url, designId) {
  const urlObj = new URL(url);
  const token = urlObj.searchParams.get('token');
  let redirectUrl = `${baseUrl}?url=${designUrl}?redirecturl=${coohomDesignToolURl}&token=${token}&locale=en_US`;
  if (designId) {
    redirectUrl = `${baseUrl}?url=${designUrl}?designid=${designId}%26redirecturl=${coohomDesignToolURl}&token=${token}&locale=en_US`;
  }
  return redirectUrl;
}

const VisualizeCabinet = () => {
  const { data, error, isError, isLoading, isFetching } =
    useGetExternalUserRegisterStatusQuery();

  const location = useLocation();
  const isDesignWorkBench =
    location.pathname?.split('/')[location.pathname?.split('/')?.length - 1] ===
    'design-workbench';

  // const {
  //   data: userProjects,
  //   error: fetchUserProjectError,
  //   isError: isErrorFetchingProjects,
  //   isLoading: isUserProjectsLoading,
  //   isFetching: isUserProjectsFetching,
  // } = useGetUserCoohomProjectQuery();
  // console.log({ userProjects });

  const [searchParams] = useSearchParams();
  const designId = searchParams.get('designId') || null;

  const [
    syncProjects,
    {
      data: syncedData,
      isLoading: isSyncProjectsLoading,
      isFetching: isSyncProjectsFetching,
      error: syncErrorMsg,
      isError: isSyncError,
    },
  ] = useSyncProjectsWithBaruMutation();
  const notifyToast = useNotifyToast();

  // console.log('######', { syncedData });
  const navigate = useNavigate();

  const [iframeUrl, setIframeUrl] = useState(null);

  function decryptURL(encryptedUrl) {
    const bytes = CryptoJS.AES.decrypt(
      decodeURIComponent(encryptedUrl),
      'COOHOM_BARU'
    );
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  useEffect(() => {
    if (data && !isLoading && !isFetching) {
      let finalUrl = '';
      if (isDesignWorkBench) {
        finalUrl = getDesignUrl(decryptURL(data?.data?.redirectUrl), designId);
        setIframeUrl(finalUrl);
      }
    }
  }, [
    data,
    data?.data?.redirectUrl,
    designId,
    isDesignWorkBench,
    isFetching,
    isLoading,
  ]);

  useEffect(() => {
    if (isError && error) {
      notifyToast(
        'Something went wrong. Please try again after sometime',
        'coohomRegisterFail',
        'error'
      );
    }
  }, [error, isError, notifyToast, syncErrorMsg?.data?.error]);

  const handleSyncProjects = () => {
    syncProjects({ designId: designId || null });
  };

  useEffect(() => {
    if ((!isSyncProjectsFetching || !isSyncProjectsLoading) && syncedData) {
      notifyToast(
        syncedData?.message || 'Projects synced successfully',
        'projectsSync',
        'success'
      );
    }

    if (isSyncError && syncErrorMsg) {
      notifyToast(
        syncErrorMsg?.data?.error ||
          'Failed to sync projects. Please try again after sometime',
        'projectsSyncErr',
        'error'
      );
    }
  }, [
    isSyncError,
    isSyncProjectsFetching,
    isSyncProjectsLoading,
    notifyToast,
    syncErrorMsg,
    syncedData,
  ]);

  // const handleRefresh = async () => {
  //   const refreshedData = await refetch();
  //   const newUrl = decryptURL(refreshedData?.data?.data?.redirectUrl);
  //   setIframeUrl('');
  //   setIframeUrl(newUrl);
  // };

  return (
    <Box
      sx={{
        height: '80vh',
        width: '100%',
        mb: 10,
      }}
    >
      <Stack direction={'row'} sx={{ mb: 1, justifyContent: 'space-between' }}>
        {/* Will copy the user remaining(if any) projects to admin acc.  */}
        <ButtonSx
          sx={{ height: '50px' }}
          variant="outlined"
          buttonText={'Back'}
          startIcon={<WestIcon />}
          onButtonPress={() => {
            navigate(-1);
          }}
        />

        {isDesignWorkBench ? (
          <>
            <Alert severity="info" sx={{ ml: 27, mr: 2 }}>
              <Typography>
                To keep your changes, please save your project in the{' '}
                <strong>Kitchen Design Tool</strong> before syncing.
              </Typography>
            </Alert>

            <Stack direction={'row'} gap={1}>
              {/* <ButtonSx
                variant="outlined"
                buttonText={'Refresh'}
                // endIcon={<RotatingSyncIcon isLoading={isSyncProjectsFetching} />}
                disabled={isError}
                onButtonPress={handleRefresh}
              /> */}

              <ButtonSx
                sx={{ height: '50px' }}
                variant="outlined"
                buttonText={
                  isSyncProjectsFetching || isSyncProjectsLoading
                    ? 'Syncing...'
                    : 'Sync Project with Baru'
                }
                endIcon={
                  <RotatingSyncIcon
                    isLoading={isSyncProjectsFetching || isSyncProjectsLoading}
                  />
                }
                disabled={
                  isLoading ||
                  isFetching ||
                  isSyncProjectsFetching ||
                  isSyncProjectsLoading ||
                  isError
                }
                onButtonPress={handleSyncProjects}
              />
            </Stack>
          </>
        ) : null}
      </Stack>

      {isLoading || isFetching ? (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
          style={{ border: '1px solid lightgrey' }}
        >
          <CircularProgress />
        </Box>
      ) : isDesignWorkBench ? (
        <iframe
          src={iframeUrl}
          title="Cabinet Visualization Tool"
          width="100%"
          height="100%"
          style={{ border: '1px solid lightgrey' }}
        ></iframe>
      ) : (
        <Box sx={{ border: '1px solid lightgrey', height: '100%' }}>
          {/* <CustomCoohomCard projectsData={userProjects?.data} /> */}
        </Box>
      )}
      <SyncingBackdrop
        isLoading={isSyncProjectsFetching || isSyncProjectsLoading}
      />
    </Box>
  );
};

export default VisualizeCabinet;
